<template>
  <v-container v-if="myRegistration" class="pa-0">
    <v-stepper v-model="step" :items="['Branch', 'Level', 'Terms and Conditions', 'Payment']">
      <!-- Branch Step -->
      <template v-slot:item.1>
        <SignUpBranchSelectionSubView
          :selected-branch-id="selectedBranchId"
          @branch-selected="handleBranchSelected">
        </SignUpBranchSelectionSubView>
      </template>

      <!-- Level Step -->
      <template v-slot:item.2 :disabled="!selectedCard">
        <SignUpProductSelectionSubView
          :selectedVariantProductId="selectedProductVariantId"
          @level-verified="handleLevelVerified"></SignUpProductSelectionSubView>
      </template>

      <!-- Terms & Conditions Step -->
      <template v-slot:item.3 :disabled="!selectedCard">
        <TermsView v-if="!termsCompleted" @termsFormCompleted="handleTermsCompleted"></TermsView>
        <div v-else>
          <a
            :href="myRegistration.terms?.url"
            :download="myRegistration.terms?.filename"
            target="_blank"
            style="text-decoration: none">
            <v-btn block color="primary" outlined>Download Signed Terms & Conditions</v-btn></a
          >
        </div>
      </template>

      <!-- Payment Step -->
      <template v-slot:item.4 :disabled="!selectedCard">
        <ShoppingCartSubView
          :refresh="step == 4"
          @registrationFull="handleRegistrationFull"></ShoppingCartSubView>
      </template>

      <!-- Action Buttons: Previous and Next -->
      <template v-slot:actions>
        <v-btn color="primary" class="m-3" :disabled="step === 1" @click="step--"> Previous </v-btn>

        <v-btn
          v-if="step != 4"
          color="primary"
          class="float-right m-3"
          :disabled="nextButtonDisable"
          @click="step++">
          Next
        </v-btn>
      </template>
    </v-stepper>

    <v-overlay v-model="showOverlay">
      <v-card>
        <v-card-text>Sorry the branch is now full</v-card-text>
      </v-card>
    </v-overlay>
  </v-container>
</template>

<script setup lang="ts">
import { uploadTerms } from '@/controllers';
import { computed, ref, watch } from 'vue';
import { useRegistrationStore } from '@/stores';
import TermsView from './subviews/SignUpTermsSubView.vue';
import ShoppingCartSubView from './subviews/ShoppingCartSubView.vue';
import SignUpBranchSelectionSubView from './subviews/SignUpBranchSelectionSubView.vue';
import SignUpProductSelectionSubView from './subviews/SignUpProductSelectionSubView.vue';

const registrationStore = useRegistrationStore();
const myRegistration = computed(() => registrationStore.getMyRegistration.data);
const selectedBranchId = computed(() => myRegistration.value?.registeredBranch()?.id ?? '');
const selectedProductVariantId = computed(
  () => myRegistration.value?.registeredProductVariant()?.id ?? '',
);
const termsCompleted = computed(() => myRegistration.value?.hasSignedTerms() ?? false);

const showOverlay = ref<boolean>(false);
const step = ref<number>(1); // Track current step
const step2disabled = ref<boolean>(true);

const nextButtonDisable = computed(() => {
  let disable = true;
  switch (step.value) {
    case 1:
      disable = selectedBranchId.value ? false : true;
      break;
    case 2:
      disable = step2disabled.value;
      break;
    case 3:
      disable = termsCompleted.value || myRegistration.value?.hasSignedTerms() ? false : true;
      break;
  }

  return disable;
});

const handleBranchSelected = async (value: string) => {
  await registrationStore.registerToBranch.mutateAsync(value);
};

const handleLevelVerified = async (passed: boolean) => {
  step2disabled.value = selectedProductVariantId.value != null && !passed;
};

const handleTermsCompleted = async (value: unknown) => {
  await uploadTerms(value as Blob);
  registrationStore.invalidateMyRegistration();
};

const handleRegistrationFull = async (value: boolean) => {
  registrationStore.clear(value);
  if (value) {
    showOverlay.value = true;
    setTimeout(async () => {
      step.value = 1;
      showOverlay.value = false; // Hide the overlay after 10 seconds
    }, 10000);
  }
};

const determineStep = async () => {
  let isFull = false;
  if (!registrationStore.ignore) {
    isFull = await registrationStore.checkIfBranchIsFull();
    registrationStore.clear(isFull);
  }
  let nextStep = 1;
  if (!isFull) {
    if (selectedBranchId.value) {
      nextStep = 2;
    }

    if (nextStep == 2 && selectedProductVariantId.value && step2disabled.value == false) {
      nextStep = 3;
    }
    if (nextStep == 3 && termsCompleted.value == true) {
      nextStep = 4;
    }
  }
  step.value = nextStep;
};

watch(
  [selectedBranchId, selectedProductVariantId, step2disabled],
  async () => {
    await determineStep();
  },
  { immediate: true },
);
</script>

<style>
.v-stepper-item--selected .v-stepper-item__avatar.v-avatar,
.v-stepper-item--complete .v-stepper-item__avatar.v-avatar {
  background-color: var(--color-text);
}
</style>
